import { useConsentManagement } from '@/hooks/use-consent-management';
import { ConsentSettings } from '@/types/cookie-consent';
import { defaultConsent } from '@/utils/gtm';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  Typography,
  Link,
  Backdrop,
  Fab,
} from '@mui/material';
import React, { useState } from 'react';
import { CookieOutlined, Settings } from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import Button from '../base/button.component';

const COOKIE_CONSENT_TEXT = `Wir verwenden Cookies, um die grundlegende Funktionalität und
Sicherheit unserer Website zu gewährleisten. Mit Ihrer Zustimmung nutzen wir auch Cookies,
um unsere Website-Nutzung zu analysieren, Inhalte zu personalisieren und Ihnen relevante
Anzeigen anzubieten. Die gesammelten Informationen können mit unseren Partnern für soziale
Medien, Werbung und Analysen geteilt werden.`;

const PRIVACY_POLICY_LINK = 'https://www.elink-distribution.com/datenschutz/';

interface CookieOptionProps {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
  description: string;
}

const CookieOption: React.FC<CookieOptionProps> = ({
  checked,
  disabled = false,
  onChange,
  label,
  description,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <FormControlLabel
      sx={{
        width: '100%',
        justifyContent: 'space-between',
        margin: 0,
        '.MuiFormControlLabel-label': {
          flex: 1,
        },
      }}
      labelPlacement="start"
      control={
        <Switch checked={checked} disabled={disabled} onChange={onChange} />
      }
      label={label}
    />
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {description}
    </Typography>
  </Box>
);

export const CookieConsentBanner: React.FC = () => {
  const { consentState, setConsentState } = useConsentManagement();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showDetails, setShowDetails] = useState(isMobile);
  const [tempSettings, setTempSettings] = useState<ConsentSettings>({
    ...defaultConsent,
    ad_storage: 'granted',
    analytics_storage: 'granted',
    functionality_storage: 'granted',
    personalization_storage: 'granted',
  });

  const cookieOptions = [
    {
      key: 'functionality_storage' as keyof ConsentSettings,
      label: 'Notwendig',
      description:
        'Diese Cookies sind für den Betrieb der Website erforderlich. Sie sichern grundlegende Funktionen wie Sicherheit und Navigation. Diese Cookies speichern keine persönlichen Daten.',
      disabled: true,
      checked: true,
    },
    {
      key: 'security_storage' as keyof ConsentSettings,
      label: 'Sicherheit',
      description:
        'Diese Cookies sind für die Sicherheit der Website unerlässlich und können nicht deaktiviert werden.',
      disabled: true,
      checked: true,
    },
    {
      key: 'personalization_storage' as keyof ConsentSettings,
      label: 'Präferenzen',
      description:
        'Diese Cookies speichern Ihre Einstellungen wie Sprache und Region, um Ihnen eine personalisierte Nutzung zu ermöglichen.',
      checked: tempSettings.personalization_storage === 'granted',
    },
    {
      key: 'analytics_storage' as keyof ConsentSettings,
      label: 'Statistiken',
      description:
        'Diese Cookies sammeln anonyme Daten darüber, wie Besucher unsere Website nutzen. Dies hilft uns, die Website stetig zu verbessern.',
      checked: tempSettings.analytics_storage === 'granted',
    },
    {
      key: 'ad_storage' as keyof ConsentSettings,
      label: 'Marketing',
      description:
        'Diese Cookies ermöglichen personalisierte Werbung auf unserer und anderen Websites. Sie helfen uns, die Relevanz und Häufigkeit von Anzeigen zu steuern.',
      checked: tempSettings.ad_storage === 'granted',
    },
  ];

  const handleAcceptAll = () => {
    const allGranted: ConsentSettings = {
      ...defaultConsent,
      ad_storage: 'granted',
      analytics_storage: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
    };
    setConsentState({ hasConsent: true, settings: allGranted });
    setShowDetails(false); // Add this line
  };

  const handleRejectAll = () => {
    setConsentState({ hasConsent: true, settings: defaultConsent });
    setShowDetails(false); // Add this line
  };

  const handleToggle = (key: keyof ConsentSettings) => {
    // Prevent toggling security and functionality cookies
    if (key === 'security_storage' || key === 'functionality_storage') return;

    setTempSettings((prev) => ({
      ...prev,
      [key]: prev[key] === 'granted' ? 'denied' : 'granted',
    }));
  };

  const handleAllowSelection = () => {
    setConsentState({ hasConsent: true, settings: tempSettings });
    setShowDetails(false); // Add this line
  };

  // Update handler for FAB click
  const handleOpenSettings = () => {
    setShowDetails(true);
    // Reset temp settings to current consent settings
    setTempSettings(consentState.settings); // This ensures switches reflect current settings
  };

  if (consentState.hasConsent) {
    return (
      <>
        <Fab
          size="medium"
          color="primary"
          aria-label="cookie-settings"
          onClick={handleOpenSettings}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            zIndex: 1000,
          }}
        >
          <CookieOutlined />
        </Fab>

        <Dialog
          open={showDetails}
          onClose={() => !isMobile && setShowDetails(false)}
          maxWidth="md"
          fullWidth
          sx={{
            zIndex: 1202,
            '& .MuiDialog-paper': {
              margin: isMobile ? 0 : undefined,
              width: isMobile ? '100%' : undefined,
              maxHeight: isMobile ? '100%' : undefined,
              borderRadius: isMobile ? 0 : undefined,
            },
          }}
        >
          <DialogTitle>
            {isMobile && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <CookieOutlined />
                <Typography variant="h6">
                  Diese Webseite verwendet Cookies
                </Typography>
              </Box>
            )}
            {!isMobile && 'Cookie-Einstellungen'}
          </DialogTitle>
          <DialogContent>
            {isMobile && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {COOKIE_CONSENT_TEXT}
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Weitere Informationen finden Sie in unserer{' '}
                  <Link href={PRIVACY_POLICY_LINK} color="primary">
                    Datenschutzerklärung
                  </Link>
                  .
                </Typography>
              </>
            )}
            <FormGroup>
              {cookieOptions.map(
                ({ key, label, description, checked, disabled }, index) => (
                  <React.Fragment key={key}>
                    <CookieOption
                      checked={checked}
                      disabled={disabled}
                      onChange={() => handleToggle(key)}
                      label={label}
                      description={description}
                    />
                    {index < cookieOptions.length - 1 && (
                      <Divider sx={{ my: 2 }} />
                    )}
                  </React.Fragment>
                ),
              )}
            </FormGroup>
          </DialogContent>
          <DialogActions
            sx={{
              p: isMobile ? 2 : 3,
              flexDirection: isMobile ? 'column' : 'row',
              gap: 1,
              '& .MuiButton-root': {
                margin: 0,
              },
            }}
          >
            <Button
              onClick={handleRejectAll}
              size="large"
              fullWidth
              variant="outlined"
              sx={{
                height: 48,
              }}
            >
              Ablehnen
            </Button>
            <Button
              onClick={handleAllowSelection}
              size="large"
              fullWidth
              variant="outlined"
              sx={{
                height: 48,
              }}
            >
              Auswahl erlauben
            </Button>
            <Button
              onClick={handleAcceptAll}
              size="large"
              fullWidth
              variant="contained"
              sx={{
                height: 48,
              }}
            >
              Alle zulassen
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Backdrop
        open={!consentState.hasConsent}
        sx={{
          zIndex: 1200, // Lower z-index
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        }}
      />
      {!isMobile && (
        <Box
          sx={{
            position: 'fixed',
            bottom: showDetails ? '-100%' : 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            px: 2,
            pt: 4,
            pb: 2,
            boxShadow: 3,
            zIndex: 1201, // Above backdrop
            transition: 'bottom 0.3s ease-in-out',
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, md: 6 },
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <CookieOutlined />
                  <Typography variant="h6">
                    Diese Webseite verwendet Cookies
                  </Typography>
                </Box>
                <Typography variant="body2">{COOKIE_CONSENT_TEXT}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Weitere Informationen finden Sie in unserer{' '}
                  <Link href={PRIVACY_POLICY_LINK} color="primary">
                    Datenschutzerklärung
                  </Link>
                  .
                </Typography>
              </Box>

              <Stack
                direction="column"
                spacing={1}
                sx={{
                  alignSelf: 'center',
                  width: { xs: 'auto', md: '240px' },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleAcceptAll}
                  size="large"
                  fullWidth
                >
                  Alle akzeptieren
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleAllowSelection}
                  size="large"
                  fullWidth
                >
                  Auswahl erlauben
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRejectAll}
                  size="large"
                  fullWidth
                >
                  Ablehnen
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormGroup sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                  {cookieOptions.map(({ key, label, checked, disabled }) => (
                    <FormControlLabel
                      key={key}
                      slotProps={{
                        typography: {
                          variant: 'subtitle2',
                        },
                      }}
                      control={
                        <Switch
                          checked={checked}
                          disabled={disabled}
                          onChange={() => handleToggle(key)}
                        />
                      }
                      label={label}
                    />
                  ))}
                </Box>
              </FormGroup>
              <Button
                variant="text"
                onClick={() => setShowDetails(true)}
                size="small"
                startIcon={<Settings />}
              >
                Einstellungen
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog
        open={showDetails || isMobile}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          if (!isMobile) setShowDetails(false);
        }}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 1202, // Highest z-index
          '& .MuiDialog-paper': {
            margin: isMobile ? 0 : undefined,
            width: isMobile ? '100%' : undefined,
            maxHeight: isMobile ? '100%' : undefined,
            borderRadius: isMobile ? 0 : undefined,
          },
        }}
        slotProps={{ backdrop: { invisible: true } }} // We're using our custom backdrop
      >
        <DialogTitle>
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CookieOutlined />
              <Typography variant="h6">
                Diese Webseite verwendet Cookies
              </Typography>
            </Box>
          )}
          {!isMobile && 'Cookie-Einstellungen'}
        </DialogTitle>
        <DialogContent>
          {isMobile && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {COOKIE_CONSENT_TEXT}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Weitere Informationen finden Sie in unserer{' '}
                <Link href={PRIVACY_POLICY_LINK} color="primary">
                  Datenschutzerklärung
                </Link>
                .
              </Typography>
            </>
          )}
          <FormGroup>
            {cookieOptions.map(
              ({ key, label, description, checked, disabled }, index) => (
                <React.Fragment key={key}>
                  <CookieOption
                    checked={checked}
                    disabled={disabled}
                    onChange={() => handleToggle(key)}
                    label={label}
                    description={description}
                  />
                  {index < cookieOptions.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </React.Fragment>
              ),
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions
          sx={{
            p: isMobile ? 2 : 3,
            flexDirection: isMobile ? 'column' : 'row',
            gap: 1,
            '& .MuiButton-root': {
              margin: 0,
            },
          }}
        >
          <Button
            onClick={handleRejectAll}
            size="large"
            fullWidth
            variant="outlined"
            sx={{
              height: 48,
            }}
          >
            Ablehnen
          </Button>
          <Button
            onClick={handleAllowSelection}
            size="large"
            fullWidth
            variant="outlined"
            sx={{
              height: 48,
            }}
          >
            Auswahl erlauben
          </Button>
          <Button
            onClick={handleAcceptAll}
            size="large"
            fullWidth
            variant="contained"
            sx={{
              height: 48,
            }}
          >
            Alle zulassen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
